import { isKgUnit } from './uom';
import type { SalesOrderStatus } from './sales-order';

/**
 * P11 B1 – Phiếu giao hàng (docs/phases/P11.md).
 * - Phiếu lập từ đơn bán Đã xác nhận / Giao một phần; giao từng phần (DEC-006 mặc định): mỗi phiếu chọn dòng + số lượng.
 * - Hàng cân (ĐVT Kg, hoặc ĐVT chính của mã là Kg) giao lệch ±3% vẫn tính là giao đủ dòng (DEC-006).
 * - Xe lấy từ danh mục xe giao hàng P06 B6 (delivery_vehicles); P11 không tạo bảng xe. Tài xế gõ tay tới khi P12 có danh mục tài xế.
 * - Đơn bán tự chuyển Giao một phần / Đã giao đủ khi phiếu "Đã giao"; Hoàn thành vẫn bấm tay (sau khi xuất hóa đơn bên Asia).
 * - Asia giữ tồn thật (DEC-001 A): phiếu giao KHÔNG trừ tồn; phiếu xuất kho vẫn lập bên Asia.
 */
export const DELIVERY_CODE_SUFFIX = 'GH';

export const DELIVERY_STATUSES = ['draft', 'in_transit', 'delivered', 'failed', 'cancelled'] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

export const DELIVERY_STATUS_LABELS: Record<DeliveryStatus, string> = {
  draft: 'Chờ giao',
  in_transit: 'Đang giao',
  delivered: 'Đã giao',
  failed: 'Giao không thành',
  cancelled: 'Đã hủy',
};

/**
 * P11 B5 PR-C: Đang giao → Giao không thành (khách không nhận, sai địa chỉ…): số trên phiếu trả lại phần còn phải giao của đơn,
 * phiếu xuất kho ERP của phiếu bị hủy (hàng về lại kho); lập phiếu giao lại từ phiếu này.
 */
export const DELIVERY_TRANSITIONS: Record<DeliveryStatus, readonly DeliveryStatus[]> = {
  draft: ['in_transit', 'delivered', 'cancelled'],
  in_transit: ['delivered', 'failed', 'cancelled'],
  delivered: [],
  failed: [],
  cancelled: [],
};

export function canTransitionDelivery(from: DeliveryStatus, to: DeliveryStatus): boolean {
  return DELIVERY_TRANSITIONS[from].includes(to);
}

/** Phiếu còn mở: số lượng trên phiếu đang "giữ chỗ" phần còn lại của đơn. */
export const DELIVERY_OPEN_STATUSES: readonly DeliveryStatus[] = ['draft', 'in_transit'];

/** Đơn bán lập được phiếu giao. */
export const DELIVERABLE_ORDER_STATUSES: readonly SalesOrderStatus[] = ['confirmed', 'partially_delivered'];

export const DELIVERY_METHODS = ['company_vehicle', 'hired_vehicle', 'customer_pickup'] as const;
export type DeliveryMethod = (typeof DELIVERY_METHODS)[number];

export const DELIVERY_METHOD_LABELS: Record<DeliveryMethod, string> = {
  company_vehicle: 'Xe công ty',
  hired_vehicle: 'Xe thuê ngoài',
  customer_pickup: 'Khách tự lấy',
};

/**
 * Giao 3 bên (anh Tiến 19/10/2026): xe thuê ngoài phải ghi rõ bên vận chuyển là Công ty hay Cá nhân.
 * Công ty: tên công ty (Lalamove, nhà xe…); Cá nhân: chủ xe chính là tài xế.
 */
export const DELIVERY_CARRIER_PARTIES = ['company', 'person'] as const;
export type DeliveryCarrierParty = (typeof DELIVERY_CARRIER_PARTIES)[number];

export const DELIVERY_CARRIER_PARTY_LABELS: Record<DeliveryCarrierParty, string> = {
  company: 'Công ty',
  person: 'Cá nhân',
};

/**
 * Thông tin bên vận chuyển còn thiếu trước khi bàn giao hàng cho tài xế (rỗng = đủ).
 * - Xe công ty: số xe + họ tên tài xế.
 * - Xe thuê: Công ty / Cá nhân, tên công ty (nếu Công ty), số xe, họ tên tài xế, SĐT tài xế.
 * - Khách tự lấy: không bắt buộc (tên người nhận ghi tay trên phiếu).
 */
export function deliveryTransportMissing(n: {
  method?: string | null;
  vehiclePlate?: string | null;
  driverName?: string | null;
  driverPhone?: string | null;
  carrierParty?: string | null;
  carrierName?: string | null;
}): string[] {
  const out: string[] = [];
  if (n.method === 'customer_pickup') return out;
  if (n.method === 'hired_vehicle') {
    if (!n.carrierParty) out.push('bên vận chuyển (Công ty / Cá nhân)');
    else if (n.carrierParty === 'company' && !n.carrierName?.trim()) out.push('tên công ty vận chuyển');
  }
  if (!n.vehiclePlate?.trim()) out.push('số xe');
  if (!n.driverName?.trim()) out.push('họ tên tài xế');
  if (n.method === 'hired_vehicle' && !n.driverPhone?.trim()) out.push('SĐT tài xế');
  return out;
}

/** Dung sai hàng cân (DEC-006): ±3%. */
export const WEIGHED_TOLERANCE = 0.03;

const QTY_SCALE = 1000; // 3 số lẻ như cột numeric(18,3)
const round3 = (v: number) => Math.round(v * QTY_SCALE) / QTY_SCALE;

/** Hàng cân: dòng bán theo Kg, hoặc mã hàng có ĐVT chính là Kg. */
export function isWeighedLine(unit: string | null | undefined, baseUnit?: string | null): boolean {
  return isKgUnit(unit) || isKgUnit(baseUnit);
}

/** Số lượng còn lại chưa giao (không âm). */
export function remainingQty(ordered: number, delivered: number): number {
  return Math.max(0, round3(ordered - delivered));
}

/**
 * Tối đa còn được đưa lên phiếu mới cho 1 dòng: hàng cân cho vượt tới +3% số đặt; hàng khác không vượt số đặt.
 * `committed` = đã giao + đang nằm trên phiếu còn mở.
 */
export function maxDeliverableQty(ordered: number, committed: number, weighed: boolean): number {
  const cap = weighed ? ordered * (1 + WEIGHED_TOLERANCE) : ordered;
  return Math.max(0, round3(cap - committed));
}

/**
 * Tối thiểu phải đưa lên phiếu mới cho 1 dòng (quy định 14/10/2026 – mỗi đơn giao đủ một lần): hàng cân đủ tới 97% số đặt,
 * hàng khác đủ số đặt. `committed` = đã giao + đang nằm trên phiếu còn mở. 0 = dòng đã đủ, không bắt buộc.
 */
export function minDeliverableQty(ordered: number, committed: number, weighed: boolean): number {
  const need = weighed ? ordered * (1 - WEIGHED_TOLERANCE) : ordered;
  return Math.max(0, round3(need - committed));
}

/** Dòng đã giao đủ: hàng cân đạt ≥ 97% số đặt; hàng khác đạt đủ số đặt. */
export function lineFulfilled(ordered: number, delivered: number, weighed: boolean): boolean {
  const need = weighed ? ordered * (1 - WEIGHED_TOLERANCE) : ordered;
  return round3(delivered) >= round3(need);
}

/**
 * Trạng thái giao của đơn theo số đã giao từng dòng: mọi dòng đủ → Đã giao đủ; có giao → Giao một phần; chưa giao → giữ Đã xác nhận.
 * Dòng dịch vụ (vận chuyển) không cần giao: truyền `skip: true`.
 */
export function orderDeliveryStatus(lines: { ordered: number; delivered: number; weighed: boolean; skip?: boolean }[]): 'confirmed' | 'partially_delivered' | 'delivered' {
  const goods = lines.filter((l) => !l.skip);
  if (!goods.length) return 'confirmed';
  if (goods.every((l) => lineFulfilled(l.ordered, l.delivered, l.weighed))) return 'delivered';
  return lines.some((l) => l.delivered > 0) ? 'partially_delivered' : 'confirmed';
}

/**
 * P11 B2 – Xác nhận nhận hàng (POD).
 * - Tài xế (quyền delivery.driver, không có delivery.confirm) giao xong phải có ≥ 1 ảnh + chữ ký người nhận + tên người nhận.
 * - Văn phòng (delivery.confirm) xác nhận thay thì ảnh / chữ ký không bắt buộc (khách tự lấy, ảnh phiếu giấy chụp sau).
 */
export const DELIVERY_PHOTO_KINDS = ['goods', 'document', 'export'] as const;
export type DeliveryPhotoKind = (typeof DELIVERY_PHOTO_KINDS)[number];

export const DELIVERY_PHOTO_KIND_LABELS: Record<DeliveryPhotoKind, string> = {
  goods: 'Ảnh hàng tại chỗ giao',
  document: 'Ảnh phiếu có chữ ký',
  /** Anh Tiến 19/10/2026: thủ kho chụp lúc hàng lên xe ở từng kho = "Đã xuất" (sau khi bấm "Đủ" = "Đã soạn") */
  export: 'Ảnh xuất hàng',
};

/** Chữ ký người nhận: data URL PNG, giới hạn như chữ ký khách trên hợp đồng. */
export const DELIVERY_SIGNATURE_MAX_CHARS = 400_000;

/**
 * P11 B3 – Chia kho, hàng chờ về (docs/phases/P11.md §7).
 * Mỗi dòng phiếu giao được chia thành các nguồn lấy hàng:
 * - `stock`: lấy ở kho Asia (mã kho Asia, tồn đọc từ GET /inventory – DEC-001 A);
 * - `incoming`: chờ hàng về theo đơn mua P10 (đã đặt NCC, chưa về đủ);
 * - `short`: chưa có hàng, chưa có đơn mua → phải mua thêm, tách phiếu hoặc chọn kho tay.
 * Mỗi kho / mỗi đơn mua đang về là một "điểm lấy hàng" trên phiếu; thủ kho xác nhận đã xuất từng điểm.
 */
export const DELIVERY_SOURCE_KINDS = ['stock', 'incoming', 'short', 'direct'] as const;
export type DeliverySourceKind = (typeof DELIVERY_SOURCE_KINDS)[number];

export const DELIVERY_SOURCE_LABELS: Record<DeliverySourceKind, string> = {
  stock: 'Lấy tại kho',
  incoming: 'Chờ hàng về',
  short: 'Chưa có hàng',
  direct: 'Giao thẳng từ NCC',
};

/**
 * P11 B5 PR-C – Trả hàng sau khi đã giao: số trả mỗi dòng ≤ số thực giao − số đã trả trước đó.
 * Trả về lỗi theo dòng (rỗng = hợp lệ).
 */
export function checkReturnLines(
  items: { id: number; lineNo: number; productCode: string; delivered: number; returned: number }[],
  lines: { itemId: number; quantity: number }[],
): string[] {
  const r3 = (n: number) => Math.round(n * 1000) / 1000;
  const errors: string[] = [];
  const seen = new Set<number>();
  for (const l of lines) {
    const it = items.find((i) => i.id === l.itemId);
    if (!it) {
      errors.push('Dòng không thuộc phiếu này');
      continue;
    }
    if (seen.has(l.itemId)) errors.push(`Dòng ${it.lineNo} (${it.productCode}) nhập trùng`);
    seen.add(l.itemId);
    const max = r3(it.delivered - it.returned);
    if (l.quantity <= 0) errors.push(`Dòng ${it.lineNo} (${it.productCode}): số trả phải lớn hơn 0`);
    else if (l.quantity > max + 1e-9) errors.push(`Dòng ${it.lineNo} (${it.productCode}) trả tối đa ${max}`);
  }
  return errors;
}

/**
 * P11 B5 PR-C – Tách phiếu theo số: mỗi dòng chuyển sang phiếu mới 0 < số < số trên dòng, hoặc đúng bằng số dòng (chuyển cả dòng).
 * Phiếu cũ phải còn ít nhất 1 dòng.
 */
export function checkSplitLines(
  items: { id: number; lineNo: number; productCode: string; quantity: number }[],
  lines: { itemId: number; quantity: number }[],
): string[] {
  const errors: string[] = [];
  const moved = new Map<number, number>();
  for (const l of lines) {
    const it = items.find((i) => i.id === l.itemId);
    if (!it) {
      errors.push('Dòng không thuộc phiếu này');
      continue;
    }
    if (moved.has(l.itemId)) errors.push(`Dòng ${it.lineNo} (${it.productCode}) nhập trùng`);
    if (l.quantity <= 0) errors.push(`Dòng ${it.lineNo} (${it.productCode}): số tách phải lớn hơn 0`);
    else if (l.quantity > it.quantity + 1e-9) errors.push(`Dòng ${it.lineNo} (${it.productCode}) tách tối đa ${it.quantity}`);
    moved.set(l.itemId, l.quantity);
  }
  if (!errors.length && items.every((i) => (moved.get(i.id) ?? 0) >= i.quantity - 1e-9)) errors.push('Phiếu cũ phải giữ lại ít nhất 1 dòng. Muốn đổi cả phiếu thì sửa phiếu.');
  return errors;
}

/**
 * Kho gợi ý cho 1 dòng báo giá (DEC-331): kho đủ cả dòng (ưu tiên kho các dòng khác đã chọn), không kho nào đủ thì kho nhiều hàng nhất.
 * null = không kho nào còn hàng khả dụng.
 */
export function suggestWarehouse(need: number, warehouses: { code: string; available: number }[], preferred: ReadonlySet<string> = new Set()): string | null {
  return allocateFromWarehouses(need > 0 ? need : 1e-3, warehouses, preferred).parts[0]?.code ?? null;
}

/**
 * Chia số cần giao (ĐVT chính) cho các kho: 1 kho đủ cả dòng → chọn kho đó (ưu tiên kho phiếu đã ghé, rồi kho nhiều hàng nhất);
 * không kho nào đủ → lấy dần từ kho nhiều hàng nhất. Phần còn thiếu trả về `rest`.
 */
export function allocateFromWarehouses(
  need: number,
  warehouses: { code: string; available: number }[],
  preferred: ReadonlySet<string> = new Set(),
  /** DEC-331: kho NV chọn trên dòng báo giá – lấy kho này trước (đủ thì chỉ kho này), thiếu mới chia thêm kho khác */
  first: string | null = null,
): { parts: { code: string; quantity: number }[]; rest: number } {
  const r3 = (n: number) => Math.round(n * 1000) / 1000;
  const chosen = first ? warehouses.find((w) => w.code === first && w.available > 0) : undefined;
  if (chosen && need > 0) {
    const q = r3(Math.min(chosen.available, need));
    const more = allocateFromWarehouses(r3(need - q), warehouses.filter((w) => w !== chosen), preferred);
    return { parts: [{ code: chosen.code, quantity: q }, ...more.parts], rest: more.rest };
  }
  let rest = r3(need);
  const avail = warehouses.filter((w) => w.available > 0).sort((a, b) => b.available - a.available || a.code.localeCompare(b.code));
  if (rest <= 0) return { parts: [], rest: 0 };
  const full = avail.filter((w) => w.available >= rest);
  if (full.length) {
    const pick = full.find((w) => preferred.has(w.code)) ?? full[0]!;
    return { parts: [{ code: pick.code, quantity: rest }], rest: 0 };
  }
  const parts: { code: string; quantity: number }[] = [];
  for (const w of avail) {
    if (rest <= 0) break;
    const q = r3(Math.min(w.available, rest));
    parts.push({ code: w.code, quantity: q });
    rest = r3(rest - q);
  }
  return { parts, rest };
}

/**
 * Sức chứa còn lại của 1 dòng đơn mua cho phiếu giao mới (ĐVT chính):
 * phần chưa về và chưa gán cho phiếu giao nào. Hàng đã về mà chưa gán phiếu thì nằm trong tồn kho Asia, không tính ở đây.
 */
export function incomingCapacity(ordered: number, received: number, allocatedOpen: number): number {
  const r3 = (n: number) => Math.round(n * 1000) / 1000;
  return Math.max(0, r3(ordered - received - Math.max(0, allocatedOpen - received)));
}

/**
 * P11 mục 13 (thiết kế anh Tiến duyệt 14/10/2026): SO3 → ERP tự lập phiếu giao nháp, kho báo Đủ / Thiếu từng điểm kho.
 * Chờ soạn → Đủ (= "Đã xuất kho", tự sinh phiếu xuất kho ERP) hoặc → Thiếu (ghi số thực có từng dòng). Thiếu rồi bấm Đủ khi đã có hàng.
 */
export const PICK_STATUSES = ['pending', 'ok', 'short'] as const;
export type PickStatus = (typeof PICK_STATUSES)[number];
export const PICK_STATUS_LABELS: Record<PickStatus, string> = { pending: 'Chờ soạn', ok: 'Đủ', short: 'Thiếu' };

/** Anh Tiến 24/10/2026: kho bấm "Nhờ in" gửi NV kinh doanh in phiếu soạn – chỉ điểm kho từ 9 mã trở lên (ít mã thì không cần in). */
export const PICK_PRINT_REQUEST_MIN_CODES = 9;

/** Ghi chú trên phiếu giao ERP tự lập khi ra SO3. */
export const DELIVERY_AUTO_SO3_NOTE = 'Tự lập khi ra SO3';

/**
 * Anh Tiến 14/10/2026: đơn đã SO3 phải có đủ địa chỉ giao, người nhận, SĐT người nhận; thiếu thì chưa xuất bến, nhắc NV phụ trách.
 * Khách tự lấy không cần địa chỉ giao. Trả về tên các mục còn thiếu (rỗng = đủ).
 */
export function deliveryContactMissing(n: {
  method?: string | null;
  deliveryAddress?: string | null;
  receiverName?: string | null;
  receiverPhone?: string | null;
}): string[] {
  // Anh Tiến 21/10/2026: khách lấy tại kho không cần địa chỉ giao, người nhận, SĐT (ảnh giao khách là chứng từ nhận hàng)
  if (n.method === 'customer_pickup') return [];
  const out: string[] = [];
  if (!n.deliveryAddress?.trim()) out.push('địa chỉ giao');
  if (!n.receiverName?.trim()) out.push('người nhận');
  if (!n.receiverPhone?.trim()) out.push('SĐT người nhận');
  return out;
}

/**
 * DEC-690 (anh Tiến 01/11/2026): sổ đơn đã SO3 còn thiếu địa chỉ giao / người nhận / SĐT – "ngày nào xong ngày đó".
 * Thành công = đủ trước hết ngày SO3 trên Asia; Thiếu = quá ngày chưa đủ; Trong ngày = chưa tới hạn; Hủy = phiếu hủy trước hạn (không tính).
 */
export const CONTACT_GAP_STATUSES = ['pending', 'completed', 'missing', 'cancelled'] as const;
export type ContactGapStatusCode = (typeof CONTACT_GAP_STATUSES)[number];
export const CONTACT_GAP_STATUS_LABELS: Record<ContactGapStatusCode, string> = {
  pending: 'Trong ngày',
  completed: 'Thành công',
  missing: 'Thiếu',
  cancelled: 'Hủy phiếu',
};
export const CONTACT_GAP_UNITS = ['month', 'quarter', 'year'] as const;
export type ContactGapUnit = (typeof CONTACT_GAP_UNITS)[number];
